import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Loader2, Clock, UserCheck, CircleDollarSign, CheckCircle2 } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const REASON = {
  needs_onboarding: { label: "Needs Connect onboarding", style: "bg-amber-100 text-amber-800" },
  below_threshold: { label: "Below $50 threshold", style: "bg-indigo-100 text-indigo-800" },
  ready: { label: "Ready to pay", style: "bg-emerald-100 text-emerald-800" },
};

export default function AdminPayoutWaitlist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState("");

  const remind = async (id) => {
    setReminding(id);
    try { await api.post(`/admin/affiliates/${id}/payout-reminder`); toast.success("Onboarding reminder emailed."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReminding(""); }
  };

  useEffect(() => {
    api.get("/admin/affiliates/payout-waitlist")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const t = data?.totals || {};
  const entries = data?.entries || [];

  return (
    <div className="space-y-6" data-testid="admin-payout-waitlist">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Affiliate Payout Waitlist</h1>
        <p className="text-sm text-muted-foreground mt-1">Approved affiliates carrying a pending commission balance that can't be paid out yet — either onboarding to Stripe Connect is incomplete, or they're below the {money(data?.min_payout_cents)} payout threshold.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="stat-onboarding">
          <div className="flex items-center gap-2 text-amber-700"><UserCheck className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Needs onboarding</span></div>
          <p className="text-3xl font-bold mt-2">{t.needs_onboarding || 0}</p>
        </Card>
        <Card className="p-4" data-testid="stat-threshold">
          <div className="flex items-center gap-2 text-indigo-700"><Clock className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Below threshold</span></div>
          <p className="text-3xl font-bold mt-2">{t.below_threshold || 0}</p>
        </Card>
        <Card className="p-4" data-testid="stat-ready">
          <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Ready to pay</span></div>
          <p className="text-3xl font-bold mt-2">{t.ready || 0}</p>
        </Card>
        <Card className="p-4" data-testid="stat-pending">
          <div className="flex items-center gap-2 text-zinc-700"><CircleDollarSign className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Total pending</span></div>
          <p className="text-3xl font-bold mt-2">{money(t.pending_cents)}</p>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6" data-testid="waitlist-empty">No affiliates are waiting on a payout right now.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Affiliate</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3 text-right">Pending</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Connect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`waitlist-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{e.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{e.email}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{e.code}</td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {money(e.pending_cents)}
                      {e.reason === "below_threshold" && e.shortfall_cents > 0 && (
                        <span className="block text-[11px] font-normal text-indigo-700">{money(e.shortfall_cents)} to go</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${REASON[e.reason]?.style}`}>{REASON[e.reason]?.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {e.payouts_enabled ? "Payouts enabled" : e.has_stripe_account ? "Started, incomplete" : "Not started"}
                      {e.reason === "needs_onboarding" && (
                        <Button size="sm" variant="outline" className="ml-2 h-7" onClick={() => remind(e.id)} disabled={reminding === e.id} data-testid={`remind-${e.id}`}>
                          {reminding === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1" />Remind</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
